import { useRef, ReactNode, CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { MdClose } from 'react-icons/md';
import { useWindowSize } from 'react-use';

import {
  useOverlay,
  useModal,
  useDialog,
  useFocusRing,
  usePreventScroll,
  mergeProps,
  OverlayContainer,
  FocusScope,
  FocusRing,
  VisuallyHidden,
  useInteractOutside,
} from 'react-aria';

import Icon from '../Icon';

export type Props = {
  title: string;
  children: ReactNode;
  showCloseButton?: boolean;
  large?: boolean;
  maxWidth?: number;

  style?: CSSProperties;
  onClose: () => void;
};

// Based on https://react-spectrum.adobe.com/react-aria/useDialog.html

export default function Modal({
  title,
  children,
  onClose,
  style,
  showCloseButton,
  large,
  maxWidth,
  ...rest
}: Props) {
  const ref = useRef<any>(null);
  const { modalProps } = useModal();
  const { dialogProps, titleProps } = useDialog({}, ref);
  const { isFocusVisible, focusProps } = useFocusRing();

  const { width } = useWindowSize();

  const shouldCloseOnInteractOutside = (element: Element) => {
    return element.getAttribute('id') === 'overlay';
  };

  const { overlayProps } = useOverlay(
    {
      onClose,
      isOpen: true,
      isDismissable: false,
    },
    ref
  );

  useInteractOutside({
    ref,
    onInteractOutsideStart: e => {
      if (shouldCloseOnInteractOutside(e.target as Element)) {
        onClose();
      }
    },
    // onInteractOutsideStart will not be called if onInteractOutside is not passed!
    onInteractOutside: () => {
      // Intentionally empty: handling happens in onInteractOutsideStart.
    },
  });

  const wrapperProps = mergeProps(
    overlayProps,
    dialogProps,
    focusProps,
    rest,
    modalProps
  ) as any;

  usePreventScroll();

  return (
    <OverlayContainer>
      <Backdrop
        id="overlay"
        large={large}
        // TODO: PRIO 1, remove or figure out how to smoother animation despite multiple re-renders in widget modals
        // initial={{ opacity: 0 }}
        // animate={{ opacity: 1 }}
        // exit={{ opacity: 0 }}
      >
        <FocusScope restoreFocus autoFocus>
          <Wrapper
            {...wrapperProps}
            initial={
              {
                // opacity: 0,
                // x: -(window.innerWidth / 2 - initialX) + initialWidth / 2,
                // y: -(window.innerHeight / 2 - initialY) + initialHeight / 2,
                // width: initialWidth,
                // height: initialHeight,
              }
            }
            animate={{
              opacity: 1,
              scale: 1,
              x: 0,
              y: 0,
              rotate: 0,
            }}
            style={{
              width: large ? (width > 1024 ? '90%' : '100%') : 'unset',
              height: large ? (width > 1024 ? '90%' : '100%') : 'unset',
              maxWidth: maxWidth ? `${maxWidth}px` : '1600px',
            }}
            exit={{ opacity: 0, scale: 0, rotate: -15 }}
            ref={ref}
          >
            <VisuallyHidden>
              <h3 {...titleProps}>{title}</h3>
            </VisuallyHidden>

            <Content style={style} large={large}>
              {children}

              {showCloseButton && (
                <FocusRing focusRingClass="modal-close-button-focus">
                  <CloseButton onClick={onClose}>
                    <Icon icon={MdClose} size={14} color="text" />
                  </CloseButton>
                </FocusRing>
              )}
            </Content>

            <ContentBackground layout focused={isFocusVisible} />
          </Wrapper>
        </FocusScope>
      </Backdrop>
    </OverlayContainer>
  );
}

const Backdrop = styled(motion.div)<{ large?: boolean; id?: string }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1001;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  ${p =>
    p.large
      ? `${p.theme.media.tabletDown} {
      align-items: flex-end;
    }`
      : ''}
`;

const Wrapper = styled(motion.div)`
  outline: none;
  position: relative;
  display: flex;
  border-radius: ${p => p.theme.radii.normal}px;
  overflow: hidden;
  max-height: 90%;
`;

const Content = styled(motion.div)<{ large?: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  flex-direction: column;

  ${p =>
    p.large
      ? `${p.theme.media.tabletDown} {
      width: 100vw;
      height: 95vh;
    }`
      : ''}
`;

const ContentBackground = styled(motion.div)<{ focused: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
  background-color: ${p => p.theme.colors.surface};
  ${p => p.theme.shadows.large}

  ${p =>
    p.focused &&
    css`
      box-shadow: 0px 0px 0px 2px ${p => p.theme.colors.primary};
    `}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
  transition: transform 100ms ease;

  &.modal-close-button-focus {
    box-shadow: 0px 0px 0px 2px ${p => p.theme.colors.primary};
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;
